import React, { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { classNames } from '@wajeez-web/core/utils';
import { Transition, Dialog } from '@headlessui/react';
import { IconType } from 'react-icons';
import { CommonActions } from '@wajeez-web/stores/common';
import { XIcon } from '@heroicons/react/outline';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import {
  useCommonDispatch,
  useCommonSelector,
} from '@wajeez-web/stores/common';

export interface TabsNavigationProps {
  text: string;
  disabled: boolean;
  desc?: string;
  Icon?: IconType;
  key?: string;
  href?: string;
  subTabs?: { text: string; id?: number | string; href: string }[];
}

export interface TabsComponentProps {
  tabs: TabsNavigationProps[];
  selectedTab: number;
  setSelectedTab: (selected: number) => void;
  onTabClick?: () => void;
}

const TabsContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { innerSideBarCollapsed } = useCommonSelector(
    (state) => state.common.settings
  );
  const dispatch = useCommonDispatch();
  return (
    <>
      <Transition.Root show={innerSideBarCollapsed} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 flex z-40 md:hidden"
          open={innerSideBarCollapsed}
          onClose={() => dispatch(CommonActions.toggleCollapsedInnerSideBar())}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600/60" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative  flex flex-col max-w-xs bg-white">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 ">
                  <button
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none "
                    onClick={() =>
                      dispatch(CommonActions.toggleCollapsedInnerSideBar())
                    }
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XIcon
                      className="h-6 w-6 text-gray-400"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </Transition.Child>

              <div className="flex  h-screen  bg-white py-3 ">{children}</div>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <Transition.Root
        enter="transition-opacity duration-75"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        show={!innerSideBarCollapsed}
        as={Fragment}
      >
        <div className="hidden  md:flex md:flex-shrink-0">{children}</div>
      </Transition.Root>
    </>
  );
};
const TabsNavigation: React.FC<TabsComponentProps> = ({ tabs }) => {
  const location = useLocation();

  return (
    <TabsContainer>
      <div className="flex flex-col items-center h-full overflow-hidden shadow-lg text-white dark bg-white print-hidden w-52  ltr:border-r rtl:border-l border-warmGray-200">
        <div className="w-full px-2 justify-between h-full flex flex-col  hover:scrollbar-thumb-gray-700 scrollbar-track-transparent scrollbar-thin">
          <div className="flex flex-col items-center w-full  border-gray-700">
            <nav className=" space-y-2 w-full  py-3">
              {tabs.map((item, i) => (
                <div className="space-y-2" key={item.text + i}>
                  {item.subTabs?.length ? (
                    <div>
                      <div
                        key={item.text}
                        className={classNames(
                          `text-primary`,
                          ` text-lg  space-x-2 px-4 py-2 font-semibold `
                        )}
                      >
                        <ReturnTranslatedText
                          origintext={item.text}
                          text={`sidebar.${item.text}.title`}
                        />
                      </div>
                      <div className="space-y-2  ">
                        {item.subTabs.map((ele) => (
                          <Link key={ele.text + i} to={ele.href}>
                            <div
                              key={ele.text}
                              className={classNames(
                                ele.href === location.pathname
                                  ? ` bg-warmGray-100 text-textPrimary`
                                  : `hover:text-textPrimary hover:bg-warmGray-100`,
                                ` w-full flex flex-row  outline-none  space-x-2 px-4 py-2 text-base align-baseline  font-medium text-gray-500 rounded-md`
                              )}
                            >
                              <ReturnTranslatedText
                                origintext={ele.text}
                                text={`sidebar.${item.text}.${ele.text}`}
                              />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link key={item.text + i} to={item.href || ''}>
                      <div
                        key={item.text}
                        className={classNames(
                          item.href === location.pathname
                            ? `  bg-warmGray-100 text-textPrimary`
                            : `hover:text-textPrimary hover:bg-warmGray-100`,
                          `w-full flex flex-row  outline-none  space-x-2 px-4 py-2 text-base align-baseline  font-medium text-gray-500 rounded-md `
                        )}
                      >
                        <ReturnTranslatedText
                          origintext={item.text}
                          text={`sidebar.${item.text}`}
                        />
                      </div>
                    </Link>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </TabsContainer>
    // <Tab.Group
    //   defaultIndex={selectedTab}
    //   onChange={(selected) => setSelectedTab(selected)}
    //   vertical={true}
    // >
    //   <Tab.List className="flex flex-col w-full align-baseline justify-start text-left rounded-xl space-y-2">
    //     {tabs.map((item, i) =>
    //       item.subTabs ? (
    //         <div key={item.text + i}>
    //           <div className=" text-primary text-base  space-x-2 px-4 py-2 font-semibold">
    //             {item.text}
    //           </div>
    //           {item.subTabs.map((subtab) => (
    //             <Tab
    //               key={subtab.text + item.text}
    //               className={({ selected }) =>
    //                 classNames(
    //                   "w-full flex flex-row  outline-none  space-x-2 px-4 py-2 text-base align-baseline  font-medium text-gray-500 rounded-md",
    //                   " text-left  ",
    //                   selected
    //                     ? " bg-warmGray-100 text-textPrimary "
    //                     : "hover:text-textPrimary hover:bg-warmGray-100"
    //                 )
    //               }
    //             >
    //               {/* <UserIcon className='h-5 w-5 align-baseline'/> */}

    //               <div> {subtab.text}</div>
    //             </Tab>
    //           ))}
    //         </div>
    //       ) : (
    //         <Tab
    //           key={item.text}
    //           className={({ selected }) =>
    //             classNames(
    //               "w-full flex flex-row   space-x-2 px-4 py-2 text-base align-baseline outline-none  font-medium text-gray-500 rounded-md",
    //               " text-left  ",
    //               selectedTab === i
    //                 ? " bg-warmGray-100 text-textPrimary "
    //                 : "hover:text-textPrimary hover:bg-warmGray-100"
    //             )
    //           }
    //         >
    //           {/* <UserIcon className='h-5 w-5 align-baseline'/> */}

    //           <div> {item.text}</div>
    //         </Tab>
    //       )
    //     )}
    //   </Tab.List>
    // </Tab.Group>
  );
};

export default TabsNavigation;
